import { useSetAtom } from 'jotai';
import { useCallback, useEffect, useId } from 'react';

import { advancedSearchDateFilterInvalidAtom } from '../stateAtoms';

/**
 * Returns a stable setter that records whether this widget instance's date filter is invalid in
 * the shared atom (which disables Apply). Each mounted widget has its own key (`useId`), so
 * several rules of the same type cannot overwrite each other. The instance's key is removed on
 * unmount, since removing a rule does not otherwise clear it.
 */
export const useAdvancedSearchDateFilterInvalid = () => {
  const instanceId = useId();
  const setMap = useSetAtom(advancedSearchDateFilterInvalidAtom);

  const setIsFilterInvalid = useCallback(
    (isInvalid: boolean) => {
      // Return prev when unchanged: pickers call this from effects, so avoid needless updates.
      setMap((prev) =>
        (prev[instanceId] ?? false) === isInvalid ? prev : { ...prev, [instanceId]: isInvalid }
      );
    },
    [setMap, instanceId]
  );

  useEffect(
    () => () => {
      setMap((prev) => {
        if (!(instanceId in prev)) return prev;
        const { [instanceId]: _removed, ...rest } = prev;
        return rest;
      });
    },
    [setMap, instanceId]
  );

  return setIsFilterInvalid;
};
